import type { UseFormReturn } from 'react-hook-form';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { WorkingHoursGrid } from '@/components/onboarding/WorkingHoursGrid';
import {
  SessionScheduleEditor,
  type SessionFieldErrors,
} from '@/components/onboarding/SessionScheduleEditor';
import { RepeatableList } from '@/components/onboarding/RepeatableList';
import { Input } from '@/components/ui/input';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import type { KbProfileFormValues } from '@/hooks/useKbProfileForm';
import {
  CAPS,
  operationFieldsForModalities,
  paymentMethodsForLocale,
  showSessionSchedules,
  showWorkingHours,
} from '@/hooks/useKbProfileForm';
import type {
  Modality,
  Offer,
  OperationFields,
  SessionSchedule,
} from '@/services/kbProfileService';
import type { WorkingHoursValue } from '@/components/onboarding/WorkingHoursGrid';
import { CharCounter } from '@/components/shared/CharCounter';
import { FieldHelp } from '@/components/onboarding/FieldHelp';

interface OperationStepProps {
  form: UseFormReturn<KbProfileFormValues>;
  modalities: Modality[];
  /** F069 #6 — the offers axis gates the session-schedule editor (services
   * only); working hours stay modality-gated. */
  offers?: Offer[];
  /** BCP-47 language tag for the tenant's locale — filters locale-specific
   * payment methods (MBWay/Multibanco → PT only; Pix → BR only). */
  language?: string;
}

export function OperationStep({ form, modalities, offers = [], language }: OperationStepProps) {
  const { t, i18n } = useTranslation();
  const activeFields = operationFieldsForModalities(modalities);
  const needsHours = showWorkingHours(modalities);
  const needsSessions = showSessionSchedules(offers, modalities);
  // F160 FR3 — threaded down to SessionScheduleEditor's force-open/error-flag
  // behavior. `useFormState` (not a bare `form.formState.errors` read) so this
  // component actually re-renders when a new validation round lands (mirrors
  // CatalogStep's own `errors.catalog` subscription).
  const { errors } = useFormState({ control: form.control });
  const sessionErrors = errors.operation?.sessions as
    | Array<SessionFieldErrors | undefined>
    | undefined;
  const operation = form.watch('operation') ?? {};
  const selectedPayments = operation.payment_methods ?? [];
  const paymentOther = operation.payment_methods_other ?? [];
  const localePaymentMethods = paymentMethodsForLocale(language ?? i18n.language);

  function setOperation(partial: Partial<OperationFields>) {
    form.setValue('operation', { ...operation, ...partial }, { shouldValidate: true });
  }

  function togglePayment(method: string) {
    const current = selectedPayments;
    setOperation({
      payment_methods: current.includes(method)
        ? current.filter((m) => m !== method)
        : [...current, method],
    });
  }

  return (
    <div className="space-y-6">
      {/* Modality-conditional text fields */}
      {activeFields.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-1">
            <p className="text-sm font-medium">{t('onboarding.kbForm.step4.operation_label')}</p>
            <FieldHelp baseKey="onboarding.kbForm.step4.operation" />
          </div>
          {activeFields.map((fieldKey) => (
            <div key={fieldKey} className="space-y-1">
              <label className="text-sm font-medium" htmlFor={`op-${fieldKey}`}>
                {t(`onboarding.kbForm.operation_field_${fieldKey}`)}
              </label>
              <Input
                id={`op-${fieldKey}`}
                className="h-11"
                value={operation[fieldKey] ?? ''}
                maxLength={CAPS.operation_field}
                onChange={(e) => setOperation({ [fieldKey]: e.target.value })}
                placeholder={t(`onboarding.kbForm.operation_field_${fieldKey}_placeholder`)}
              />
              <CharCounter
                value={operation[fieldKey] ?? ''}
                max={CAPS.operation_field}
                mode="near-limit"
              />
            </div>
          ))}
        </div>
      )}

      {/* Working hours grid */}
      {needsHours && (
        <FormField
          control={form.control}
          name="operation.working_hours"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('onboarding.kb.working_hours')}</FormLabel>
              <FormControl>
                <WorkingHoursGrid
                  value={field.value as WorkingHoursValue | undefined}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {/* Session schedule editor — fixed-start recurring sessions (F058);
          gated by offers(services) + live-attendance modality (F069 #6) */}
      {needsSessions && (
        <FormField
          control={form.control}
          name="operation.sessions"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <span className="flex items-center gap-1">
                  <span>{t('onboarding.kb.sessions.title')}</span>
                  <FieldHelp baseKey="onboarding.kbForm.step4.sessions" />
                </span>
              </FormLabel>
              <p className="text-xs text-muted-foreground">{t('onboarding.kb.sessions.hint')}</p>
              <FormControl>
                <SessionScheduleEditor
                  value={field.value as SessionSchedule[] | undefined}
                  onChange={field.onChange}
                  errors={sessionErrors}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {/* Payment methods */}
      <div className="space-y-3">
        <p className="text-sm font-medium">{t('onboarding.kbForm.step4.payment_label')}</p>
        <div className="flex flex-col gap-2">
          {localePaymentMethods.map((method) => {
            const checked = selectedPayments.includes(method);
            return (
              <label
                key={method}
                className="flex items-center gap-3 min-h-11 px-3 py-2 rounded-md border border-border cursor-pointer hover:bg-muted transition-colors"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => togglePayment(method)}
                  className="h-4 w-4"
                  aria-label={t(`onboarding.kbForm.payment_${method}`)}
                />
                <span className="text-sm">{t(`onboarding.kbForm.payment_${method}`)}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Payment methods other (free-add) */}
      <div className="space-y-2">
        <p className="text-sm font-medium">{t('onboarding.kbForm.step4.payment_other_label')}</p>
        <p className="text-xs text-muted-foreground">
          {t('onboarding.kbForm.step4.payment_other_hint')}
        </p>
        <RepeatableList
          items={paymentOther}
          onChange={(items) => setOperation({ payment_methods_other: items })}
          emptyItem={() => ''}
          maxEntries={CAPS.payment_other_items}
          testId="payment-other-list"
          addLabelKey="onboarding.kbForm.step4.payment_other_add"
          renderRow={(item, _idx, update) => (
            <Input
              className="h-11"
              value={item}
              maxLength={CAPS.payment_other}
              onChange={(e) => update(e.target.value)}
              placeholder={t('onboarding.kbForm.step4.payment_other_placeholder')}
              aria-label={t('onboarding.kbForm.step4.payment_other_placeholder')}
            />
          )}
        />
      </div>
    </div>
  );
}
